/**
 * Typed client for the Laravel API.
 *
 * Auth is a Sanctum personal-access token held in localStorage and sent as a
 * bearer token, matching docs/04-api-spec.md §4. A 401 clears the token and
 * notifies listeners so the router can bounce to /login.
 */

const TOKEN_KEY = 'oneparacon.token';
const BASE_URL = import.meta.env['VITE_API_URL'] ?? '/api/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
    /** Laravel 422 field errors, keyed by input name. */
    public readonly errors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** First message for a field, for inline form display. */
  fieldError(field: string): string | undefined {
    return this.errors?.[field]?.[0];
  }
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

export const tokenStore = {
  get: (): string | null => window.localStorage.getItem(TOKEN_KEY),
  set: (token: string) => window.localStorage.setItem(TOKEN_KEY, token),
  clear: () => window.localStorage.removeItem(TOKEN_KEY),
};

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip the Authorization header — used by login and password reset. */
  anonymous?: boolean;
  query?: Record<string, string | number | boolean | undefined | null>;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, anonymous, query, headers, ...rest } = options;

  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
  }

  const token = anonymous ? null : tokenStore.get();
  const isFormData = body instanceof FormData;

  const response = await fetch(url.toString(), {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(isFormData ? {} : body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: isFormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    if (response.status === 401 && !anonymous) {
      tokenStore.clear();
      unauthorizedListeners.forEach((listener) => listener());
    }

    const error = (payload as { error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null)
      ?.error;

    throw new ApiError(
      response.status,
      error?.code ?? `http_${response.status}`,
      error?.message ??
        (payload as { message?: string } | null)?.message ??
        'Something went wrong. Please try again.',
      error?.details,
      (payload as { errors?: Record<string, string[]> } | null)?.errors,
    );
  }

  // Laravel API Resources wrap single and collection payloads in `data`.
  return (payload as { data?: T })?.data !== undefined ? (payload as { data: T }).data : (payload as T);
}

/** Same as `request` but returns the full envelope, for paginated responses. */
async function requestEnvelope<T>(path: string, options: RequestOptions = {}): Promise<Paginated<T>> {
  const { body, anonymous, query, headers, ...rest } = options;
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
    }
  }
  const token = anonymous ? null : tokenStore.get();
  const response = await fetch(url.toString(), {
    ...rest,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    if (response.status === 401 && !anonymous) {
      tokenStore.clear();
      unauthorizedListeners.forEach((listener) => listener());
    }
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(response.status, error?.code ?? `http_${response.status}`, error?.message ?? 'Request failed');
  }

  return payload as Paginated<T>;
}

export interface Paginated<T> {
  data: T[];
  meta: { current_page: number; per_page: number; total: number; last_page: number };
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  paginated: <T>(path: string, options?: RequestOptions) =>
    requestEnvelope<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
  /** Fetch a protected file as a blob — for showing an image the bearer token guards. */
  blob: async (path: string): Promise<Blob> => {
    const token = tokenStore.get();
    const response = await fetch(new URL(`${BASE_URL}${path}`, window.location.origin).toString(), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) throw new ApiError(response.status, 'download_failed', 'That file could not be loaded.');

    return response.blob();
  },
  /**
   * Save a file the API streams back — a template, a report.
   *
   * A plain link cannot carry the bearer token, so the file is fetched with it
   * and handed to the browser as a blob.
   */
  download: async (path: string, filename: string): Promise<void> => {
    const token = tokenStore.get();
    const response = await fetch(new URL(`${BASE_URL}${path}`, window.location.origin).toString(), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) throw new ApiError(response.status, 'download_failed', 'That file could not be downloaded.');

    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  },
};
